/**
 * @file Confirm 确认对话框 API
 * @description 基于 Dialog 的 Promise 式确认；关闭淡出后再清空展示内容。
 * 支持异步确认：传入 `onConfirm` 后，确认按钮点击进入 loading，动作成功后才关闭弹窗。
 *
 * @whenToUse 删除确认、不可逆操作、需等待接口返回的确认。
 * @whenNotToUse 复杂表单弹窗（直接用 Dialog）。
 */

import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { toast } from '@/components/ui/toast.tsx'

/** 与 Dialog 关闭 fade 一致 */
const CLOSE_MS = 200

type ConfirmRequest = {
  id: string
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  /** 异步确认动作；成功后才关闭弹窗，失败提示错误并保持打开 */
  onConfirm?: () => Promise<void>
  resolve: (ok: boolean) => void
}

type ConfirmState = {
  current: ConfirmRequest | null
  open: (req: Omit<ConfirmRequest, 'id' | 'resolve'>) => Promise<boolean>
  settle: (ok: boolean) => void
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  current: null,
  open: (req) =>
    new Promise<boolean>((resolve) => {
      set({
        current: {
          ...req,
          id: `confirm-${Date.now()}`,
          resolve,
        },
      })
    }),
  settle: (ok) => {
    const cur = get().current
    cur?.resolve(ok)
    set({ current: null })
  },
}))

/** 命令式确认：`const ok = await confirm({ title: '删除？' })` */
export function confirm(options: {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  /** 异步确认动作；成功后才关闭弹窗，失败提示错误并保持打开 */
  onConfirm?: () => Promise<void>
}) {
  return useConfirmStore.getState().open(options)
}

/**
 * 挂载在应用根部
 */
export function ConfirmHost() {
  const current = useConfirmStore((s) => s.current)
  const settle = useConfirmStore((s) => s.settle)
  const [open, setOpen] = useState(false)
  /** 确认动作执行中（仅 onConfirm 模式） */
  const [confirming, setConfirming] = useState(false)
  /** 关闭动画期间仍保留上一帧内容，避免标题/文案提前清空 */
  const [display, setDisplay] = useState<ConfirmRequest | null>(null)

  useEffect(() => {
    if (current) {
      setDisplay(current)
      setOpen(true)
      return
    }
    setOpen(false)
    const timer = window.setTimeout(() => setDisplay(null), CLOSE_MS)
    return () => window.clearTimeout(timer)
  }, [current])

  /**
   * 执行确认：无 onConfirm 时直接关闭；有 onConfirm 时先跑动作，
   * 成功才关闭，失败提示错误并保持弹窗打开供重试。
   */
  const handleConfirm = async () => {
    if (!display?.onConfirm) {
      settle(true)
      return
    }
    setConfirming(true)
    try {
      await display.onConfirm()
      settle(true)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '操作失败，请重试'
      toast.danger(message)
    } finally {
      setConfirming(false)
    }
  }

  /** 执行中不允许关闭（Esc / 遮罩 / 关闭按钮 / 取消均拦截） */
  const requestClose = () => {
    if (confirming) return
    settle(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
      className="max-w-93.75"
      title={display?.title ?? ''}
      footer={
        <>
          <Button
            variant="secondary"
            disabled={confirming}
            onClick={() => settle(false)}
          >
            {display?.cancelText ?? '取消'}
          </Button>
          <Button
            variant={display?.danger ? 'danger' : 'primary'}
            loading={confirming}
            onClick={handleConfirm}
          >
            {display?.confirmText ?? '确认'}
          </Button>
        </>
      }
    >
      {display?.description ? (
        <p className="text-sm text-muted">{display.description}</p>
      ) : (
        <p className="text-sm text-muted">请确认是否继续此操作。</p>
      )}
    </Dialog>
  )
}
